import deepDiff from 'deep-diff';
import Rollbar from 'rollbar';
import { v4 as uuid } from 'uuid';
import { Account, EnvironmentName, ExternalLogger, ProductEnvironment, User } from '@cld/unicorn-api';
import { ServerError } from '@cld/empty-state';
import { ConsoleLogger } from '../state/types';
import { Component, PropsWithChildren, ErrorInfo } from 'react';

interface ClientInfo {
  account: Pick<Account, 'externalId'>;
  productEnviroment: Pick<ProductEnvironment, 'externalId' | 'cloudName'>;
  user: Pick<User, 'externalId' | 'role' | 'technicalRole' | 'email'>;
  impersonationActive: boolean;
}

export interface ExternalLoggerFactory {
  createInstance: (accessToken: string) => ExternalLogger;
  updateClientInfo: (clientInfo: Partial<ClientInfo>) => void;
}

const ROLLBAR_UNHANDLED_ERRORS_TOKEN = '5eac90f773144e638089f3a0cedef475';

export const createExternalLoggerFactory = ({
  environmentName,
  consoleLogger,
}: {
  environmentName: EnvironmentName;
  consoleLogger: ConsoleLogger;
}): ExternalLoggerFactory => {
  const sessionCorrelationId = uuid();
  const clientInfo: Partial<ClientInfo> = {};
  const addClientInfo: Rollbar.Configuration['transform'] = (payload) => {
    Object.assign(payload, { clientInfo });
  };

  consoleLogger.debug('logger', 'Initializing logging tool - Rollbar');
  consoleLogger.debug('logger', 'Logging session correlation id', { sessionCorrelationId });

  if (environmentName === 'production') {
    new Rollbar({
      accessToken: ROLLBAR_UNHANDLED_ERRORS_TOKEN,
      captureUncaught: true,
      captureUnhandledRejections: true,
      uncaughtErrorLevel: 'error',
      transform: addClientInfo,
      payload: {
        environment: environmentName,
        context: sessionCorrelationId,
        client: {
          javascript: {
            guess_uncaught_frames: true,
            source_map_enabled: true,
          },
        },
      },
    });

    consoleLogger.debug('logger', 'Uncaught errors logging configured');
  }

  return {
    createInstance: (accessToken) => {
      const rollbar =
        environmentName === 'production'
          ? new Rollbar({
              accessToken,
              transform: addClientInfo,
              payload: {
                environment: environmentName,
                context: sessionCorrelationId,
                client: {
                  javascript: {
                    source_map_enabled: true,
                  },
                },
              },
            })
          : undefined;

      class ExternalLoggerErrorBoundary extends Component<PropsWithChildren & { errorBoundaryLogLabel: string }, { hasError: boolean }> {
        state = { hasError: false };

        static getDerivedStateFromError() {
          return { hasError: true };
        }

        componentDidCatch(error: Error, errorInfo: ErrorInfo) {
          consoleLogger.error('general', 'Error', { error, errorInfo, errorBoundaryLogLabel: this.props.errorBoundaryLogLabel });
          rollbar?.error(error, { componentStack: errorInfo.componentStack, errorBoundaryLogLabel: this.props.errorBoundaryLogLabel });
        }

        render() {
          if (this.state.hasError) {
            return <ServerError data-test="app-error" />;
          }

          return this.props.children;
        }
      }

      return {
        debug: (...args) => {
          if (!rollbar) {
            consoleLogger.debug('general', 'Rollbar logging disabled (due to setup or environment)', { level: 'debug', data: args });
            return;
          }

          consoleLogger.debug('general', 'Rollbar log sent', { level: 'debug', data: args });
          rollbar.debug(...args);
        },
        warning: (...args) => {
          if (!rollbar) {
            consoleLogger.debug('general', 'Rollbar logging disabled (due to setup or environment)', { level: 'warning', data: args });
            return;
          }

          consoleLogger.debug('general', 'Rollbar log sent', { level: 'warning', data: args });
          rollbar.warning(...args);
        },
        error: (...args) => {
          if (!rollbar) {
            consoleLogger.debug('general', 'Rollbar logging disabled (due to setup or environment)', { level: 'error', data: args });
            return;
          }

          consoleLogger.debug('general', 'Rollbar log sent', { level: 'error', data: args });
          rollbar.error(...args);
        },
        ErrorBoundary: ExternalLoggerErrorBoundary,
      };
    },
    updateClientInfo: (newClientInfo) => {
      const clientInfoDiff = deepDiff(newClientInfo, clientInfo) ?? [];

      if (clientInfoDiff.length > 0) {
        Object.assign(clientInfo, newClientInfo);
        consoleLogger.debug('logger', 'Logging client info updated', newClientInfo);
      }
    },
  };
};
